/**
 * Portada.
 *
 * El unico `<h1>` de la pagina es la propuesta de valor, no el nombre de la
 * tienda: es lo que Google muestra y lo que le dice a alguien que llega por
 * primera vez que esto es equipo para el rio, no una tienda outdoor generica. De ahi para abajo, cada bloque es un `<section>` con su `<h2>`.
 *
 * Las tres filas de banners (carrusel, promociones y cuadros) van FUERA del
 * `.contenedor`, de lado a lado de la pantalla, y el resto de la portada sigue
 * dentro del ancho de siempre.
 */
import { MARCA } from '../configuracion/marca';
import { SITIO } from '../configuracion/sitio';
import { Cargando, AvisoError } from '../componentes/comunes/EstadoPeticion';
import { GrillaProductos } from '../componentes/catalogo/GrillaProductos';
import { Carrusel } from '../componentes/inicio/Carrusel';
import { FilaCuadros } from '../componentes/inicio/FilaCuadros';
import { FilaPromociones } from '../componentes/inicio/FilaPromociones';
import { GrillaCategorias } from '../componentes/inicio/GrillaCategorias';
import { PanelWhatsapp } from '../componentes/inicio/PanelWhatsapp';
import { SeccionInicio } from '../componentes/inicio/SeccionInicio';
import { TiraConfianza } from '../componentes/inicio/TiraConfianza';
import { TiraMarcas } from '../componentes/inicio/TiraMarcas';
import { usePeticion } from '../hooks/use-peticion';
import { DatosEstructurados } from '../seo/DatosEstructurados';
import { esquemaOrganizacion, esquemaSitio } from '../seo/esquemas';
import { Seo } from '../seo/Seo';
import { obtenerInicio } from '../servicios/catalogo.servicio';
import { agruparBanners } from '../utilidades/banners';
import { numero } from '../utilidades/formato';
import { rutas } from '../utilidades/rutas';

/** Desde cuantas marcas vale la pena la tira de marcas de la portada. */
const MARCAS_PARA_MOSTRAR = 4;

export function Inicio() {
  const { estado, datos, error } = usePeticion((senal) => obtenerInicio(senal), []);
  const filas = agruparBanners(datos?.banners ?? []);

  return (
    <>
      <Seo
        titulo="Equipo de rafting, kayak y campamento base"
        descripcion={SITIO.descripcion}
        ruta={rutas.inicio()}
        imagen={MARCA.compartir}
      />
      <DatosEstructurados esquemas={[esquemaOrganizacion(), esquemaSitio()]} />

      <h1 className="solo-lectores">{SITIO.lema}</h1>

      {datos !== null ? (
        <div className="portada-banners">
          <Carrusel banners={filas.carrusel} />
          <FilaPromociones banners={filas.promociones} />
          <FilaCuadros banners={filas.cuadros} />
        </div>
      ) : null}

      <div className="contenedor pagina">
        {estado === 'cargando' ? <Cargando que="la portada" /> : null}
        {estado === 'error' ? <AvisoError error={error} /> : null}

        {datos !== null ? (
          <>
            <TiraConfianza />

            <SeccionInicio
              id="destacados"
              titulo="Lo más pedido"
              bajada="Equipo que usamos y recomendamos para el río"
            >
              <GrillaProductos productos={datos.destacados} titulo="Productos destacados" />
            </SeccionInicio>

            <SeccionInicio
              id="categorias"
              titulo="Encuentra tu equipo"
              bajada={`${numero(datos.resumen.lineas)} líneas · ${numero(datos.resumen.categorias)} categorías`}
            >
              <GrillaCategorias categorias={datos.categoriasDestacadas} />
            </SeccionInicio>

            {datos.ofertas.length > 0 ? (
              <SeccionInicio
                id="ofertas"
                titulo="Ofertas vigentes"
                bajada="Precios rebajados por tiempo limitado"
              >
                <GrillaProductos productos={datos.ofertas} titulo="Productos en oferta" />
              </SeccionInicio>
            ) : null}

            <PanelWhatsapp />

            {/* Con dos o tres marcas la tira se ve vacia: aparece cuando hay que mostrar. */}
            {datos.marcas.length >= MARCAS_PARA_MOSTRAR ? (
              <SeccionInicio id="marcas" titulo="Marcas que trabajamos">
                <TiraMarcas marcas={datos.marcas} />
              </SeccionInicio>
            ) : null}
          </>
        ) : null}
      </div>
    </>
  );
}
